// src/lib/recommendation-builder.ts
// PRIO 3 FIX: Build recommendations from ENGINE data, not from index-based fallbacks

import type { MasterResult } from "./master-engine";
import type { DiagnosticDimension } from "./master-diagnostic-system";
import { DIMENSION_LABELS } from "./master-diagnostic-system";

export type PriorityRecommendation = {
  rank: number;
  key: string;
  title: string;
  dimension: DiagnosticDimension;
  problem: string;
  businessImpact: string;
  rootCause: string;
  benchmarkHint: string;
  actions: string[];
  shortTermImpact: string;
  whyNow: string;
  websiteLink?: string;
  dominant: boolean;
};

export type QuickWinRecommendation = {
  title: string;
  category: string;
  effort: "low" | "medium" | "high";
  impact: "low" | "medium" | "high";
  timeframe: string;
  description: string;
  steps: string[];
};

/**
 * Builds priority recommendations from ENGINE data (dimension-based)
 * 
 * This replaces the old index-based fallback logic with causal, dimension-based recommendations.
 */
export function buildPriorityRecommendations(result: MasterResult): PriorityRecommendation[] {
  if (!result) return [];
  
  const priorities: PriorityRecommendation[] = [];
  const team = result.teamPerformance;
  const topLevers = result.topLevers || [];

  // NEUE LOGIK: Rep-Performance-Probleme haben ABSOLUTE Priorität
  if (team?.summarySignals) {
    const signals = team.summarySignals;
    
    // Priorität 1: Pacing Problem (wenn vorhanden, IMMER dominant)
    if (signals.hasPacingProblem) {
      priorities.push({
        rank: 1,
        key: "priority_pacing",
        title: "Sales Pace auf Zielkurs bringen",
        dimension: "REP_PERFORMANCE",
        problem: `Ihr Team liegt aktuell unter dem linearen Sollverlauf. Bei aktueller Pace entsteht bis Jahresende eine Ziellücke von ${formatCurrency(team.projectedYearEndGap)}.`,
        businessImpact: `Ohne Gegensteuerung wird das Jahresziel von ${formatCurrency(team.totalRevenueTargetYear)} voraussichtlich verfehlt. Erforderlicher monatlicher Aufholbedarf: ${formatCurrency(team.pacingRecoveryPotentialMonthly)}.`,
        rootCause: "Das Problem ist nicht primär der Funnel, sondern fehlende Zielsteuerung, ungleiche Rep-Performance und schwache Pipeline-Abdeckung.",
        benchmarkHint: `Aktuell ${pct(team.pacingVsPlanPct)} Pace vs. Plan. Ziel: 100% oder höher.`,
        actions: [
          "Wöchentliches Soll-Ist-Tracking auf Team- und Rep-Ebene einführen",
          "Zielrückstand pro Rep transparent machen und wöchentlich reviewen",
          "Pipeline-Abdeckung pro Rep prüfen: Wer kann sein Ziel überhaupt noch erreichen?",
          "Top-Performer-Verhalten dokumentieren und replizierbar machen",
        ],
        shortTermImpact: "Höhere Planbarkeit, weniger Überraschungen zum Jahresende, geringere Ziellücke.",
        whyNow: "Weil jeder Monat ohne Gegensteuerung die Ziellücke vergrößert und die Erreichbarkeit des Jahresziels weiter sinkt.",
        dominant: true,
      });
    }

    // Priorität 2: Target Breakdown Problem
    if (signals.hasTargetBreakdownProblem && priorities.length < 3) {
      priorities.push({
        rank: priorities.length + 1,
        key: "priority_target_breakdown",
        title: "Ziele operativ auf Reps herunterbrechen",
        dimension: "REP_PERFORMANCE",
        problem: "Das Umsatzziel ist nicht konsequent auf Reps heruntergebrochen. Das erschwert Steuerung, Verantwortlichkeit und Review.",
        businessImpact: `Geschätztes Potenzial durch bessere Zielverteilung: ${formatCurrency(team.repAlignmentPotentialMonthly)}/Monat.`,
        rootCause: "Ohne klare Rep-Ziele gibt es keine Verantwortlichkeit, keine saubere Forecast-Logik und keine belastbare Steuerung.",
        benchmarkHint: `Target Management Score: ${team.targetManagementScore}/100. Ziel: >80.`,
        actions: [
          "Jahresziel auf Quartals- und Monatsziele je Rep übersetzen",
          "Ziel-Ist-Abweichung pro Rep wöchentlich sichtbar machen",
          "Verantwortlichkeit verankern: Jeder Rep kennt sein Ziel und seinen aktuellen Stand",
        ],
        shortTermImpact: "Klarere Verantwortung, bessere Steuerbarkeit, höhere Zielerreichung.",
        whyNow: "Weil alle anderen Optimierungen unter Wert bleiben, solange Ziele nicht sauber auf Reps heruntergebrochen sind.",
        dominant: priorities.length === 0,
      });
    }

    // Priorität 3: Pipeline Problem
    if (signals.hasPipelineProblem && priorities.length < 3) {
      priorities.push({
        rank: priorities.length + 1,
        key: "priority_pipeline",
        title: "Pipeline-Abdeckung pro Rep stabilisieren",
        dimension: "REP_PERFORMANCE",
        problem: "Ein Teil der Reps verfügt nicht über ausreichend belastbare Pipeline zur Zielerreichung. Das ist ein strukturelles Risiko.",
        businessImpact: `Geschätztes Potenzial durch bessere Pipeline-Aktivierung: ${formatCurrency(team.pipelineActivationPotentialMonthly)}/Monat.`,
        rootCause: "Pipeline-Qualität, -Verteilung und -Aktivierung sind nicht systematisch gesteuert.",
        benchmarkHint: `Pipeline Readiness Score: ${team.pipelineReadinessScore}/100. Ziel: >75.`,
        actions: [
          "Pipeline-Abdeckung pro Rep berechnen: Wer hat genug Pipe zur Zielerreichung?",
          "Deals ohne Fortschritt bereinigen und Pipeline-Qualität erhöhen",
          "Pipeline-Lücken gezielt priorisieren und schließen",
        ],
        shortTermImpact: "Höhere Forecast-Sicherheit, weniger strukturelles Risiko, bessere Zielerreichung.",
        whyNow: "Weil Pipeline-Probleme sich nicht kurzfristig lösen lassen und jetzt gehandelt werden muss.",
        dominant: priorities.length === 0,
      });
    }

    // Priorität 4: Variance Problem
    if (signals.hasVarianceProblem && priorities.length < 3) {
      priorities.push({
        rank: priorities.length + 1,
        key: "priority_variance",
        title: "Top-Performer-Verhalten replizierbar machen",
        dimension: "REP_PERFORMANCE",
        problem: "Die Leistungsstreuung im Team ist hoch. Ein Teil des Potenzials liegt in Standardisierung und Coaching.",
        businessImpact: "Wenn der Durchschnitt näher an Top-Performer rückt, steigt der Gesamtumsatz messbar.",
        rootCause: "Top-Performer-Verhalten ist nicht dokumentiert, nicht trainiert und nicht repliziert.",
        benchmarkHint: `Team Execution Score: ${team.teamExecutionScore}/100. Ziel: >70.`,
        actions: [
          "Top-Performer-Verhalten dokumentieren: Was machen sie anders?",
          "Best Practices in Playbooks übersetzen",
          "Coaching und Training auf Basis von Top-Performer-Patterns aufbauen",
        ],
        shortTermImpact: "Höherer Durchschnitt, weniger Abhängigkeit von einzelnen Reps, stabilere Performance.",
        whyNow: "Weil Leistungsstreuung ein Führungs- und Standardisierungsthema ist, kein individuelles Rep-Problem.",
        dominant: priorities.length === 0,
      });
    }

    // Priorität 5: Review Problem
    if (signals.hasReviewProblem && priorities.length < 3) {
      priorities.push({
        rank: priorities.length + 1,
        key: "priority_review",
        title: "Rep-Reviews und Steuerungsdisziplin verankern",
        dimension: "PROCESS",
        problem: "Review-Rhythmus und Steuerungsdisziplin deuten auf ein Management-Thema hin.",
        businessImpact: "Ohne regelmäßige Reviews gibt es keine Früherkennung, keine Gegensteuerung und keine Verantwortlichkeit.",
        rootCause: "Fehlende Review-Disziplin führt zu späten Reaktionen und verpassten Chancen.",
        benchmarkHint: "Ziel: Wöchentliche Rep-Reviews mit Ziel, Ist, Forecast und Pipeline-Abdeckung.",
        actions: [
          "Wöchentlichen Review-Rhythmus etablieren",
          "Review-Agenda standardisieren: Ziel, Ist, Forecast, Pipeline, Blocker",
          "Verantwortlichkeit verankern: Jeder Rep kennt seinen Stand und seine nächsten Schritte",
        ],
        shortTermImpact: "Frühere Problemerkennung, schnellere Gegensteuerung, höhere Zielerreichung.",
        whyNow: "Weil ohne Review-Disziplin alle anderen Maßnahmen nicht nachhaltig wirken.",
        dominant: priorities.length === 0,
      });
    }
  }

  // Fallback: Wenn keine Rep-Performance-Daten vorhanden, nutze topLevers
  if (priorities.length === 0 && topLevers.length > 0) {
    topLevers.slice(0, 3).forEach((lever, index) => {
      const dimension = lever.dimension || "PROCESS";
      const content = getDimensionContent(dimension, result);

      priorities.push({
        rank: index + 1,
        key: `priority_${index + 1}`,
        title: lever.title,
        dimension,
        problem: lever.whyItMatters || "In diesem Bereich entsteht aktuell ein relevanter Teil der Umsatzleckage.",
        businessImpact:
          lever.annualImpact && lever.annualImpact > 0
            ? `Geschätzter Jahreshebel: ${formatCurrency(lever.annualImpact)}`
            : "Messbarer Umsatzhebel identifiziert.",
        rootCause: content.rootCause,
        benchmarkHint: content.benchmarkHint,
        actions: content.actions,
        shortTermImpact: content.shortTermImpact,
        whyNow: content.whyNow,
        websiteLink: content.websiteLink,
        dominant: index === 0,
      });
    });
  }

  return priorities.slice(0, 3);
}

/**
 * Gets dimension-specific recommendation content
 */
function getDimensionContent(
  dimension: DiagnosticDimension | undefined,
  result: MasterResult
): {
  rootCause: string;
  benchmarkHint: string;
  actions: string[];
  shortTermImpact: string;
  whyNow: string;
  websiteLink?: string;
} {
  const metrics = result.computedMetrics;
  
  switch (dimension) {
    case "DEMAND":
      return {
        rootCause: "Die Qualität, Passung oder Erwartung der eingehenden Leads ist nicht sauber genug. Das Problem beginnt vor dem ersten Gespräch.",
        benchmarkHint: `Aktuell ${pct(metrics?.convContactToDiscovery)} Kontakt→Discovery vs. Benchmark ${pct(metrics?.benchmarkContactToDiscovery)}.`,
        actions: [
          "Lead-Scoring einführen: Nur qualifizierte Leads gehen in den Sales-Prozess.",
          "Lead-Quellen analysieren: Welche Kanäle liefern die besten Leads?",
          "Erwartungsmanagement: Klare Kommunikation, was Leads vom ersten Gespräch erwarten können.",
        ],
        shortTermImpact: "Mehr qualifizierte Gespräche, weniger Zeitverschwendung mit unpassenden Leads.",
        whyNow: "Weil alle nachgelagerten Optimierungen unter Wert bleiben, solange die Lead-Qualität nicht stimmt.",
      };

    case "WEBSITE":
      // Kein Website-Case mehr — Inbound-Prozess als Ersatz
      return {
        rootCause: "Der Inbound-Qualifizierungsprozess ist nicht sauber genug strukturiert. Eingehende Kontakte werden zu langsam oder zu uneinheitlich in den Sales-Prozess überführt.",
        benchmarkHint: `Aktuell ${pct(metrics?.convContactToDiscovery)} Kontakt→Discovery vs. Benchmark ${pct(metrics?.benchmarkContactToDiscovery)}.`,
        actions: [
          "Inbound-SLA definieren: Jeder eingehende Kontakt wird innerhalb von 2 Stunden (werktags) qualifiziert.",
          "Qualifizierungsfragen standardisieren: K.O.-Kriterien und Routing-Regeln schriftlich festlegen.",
          "Follow-up-Sequenz aufbauen: Mindestens 5 Touchpoints innerhalb von 10 Werktagen.",
        ],
        shortTermImpact: "Mehr qualifizierte Discovery Calls, weniger verlorene Inbound-Kontakte.",
        whyNow: "Weil Inbound-Kontakte bereits Kaufinteresse signalisieren und zu wertvoll sind, um sie durch langsame Reaktion zu verlieren.",
      };

    case "CONVERSION":
      return {
        rootCause: "Im Funnel entstehen messbare Verluste zwischen Kontakt, Gespräch und reifer Opportunity. Die Übergänge sind nicht sauber genug.",
        benchmarkHint: `Aktuell ${pct(metrics?.convDiscoveryToPresentation)} Discovery→Präsentation vs. Benchmark ${pct(metrics?.benchmarkDiscoveryToPresentation)}.`,
        actions: [
          "Funnel-Stages klar definieren: Wann ist ein Lead qualifiziert? Wann reif für Präsentation?",
          "Conversion-Trigger identifizieren: Was bringt Leads von einer Stage zur nächsten?",
          "Bottlenecks messen: Wo verlieren wir die meisten Opportunities?",
        ],
        shortTermImpact: "Weniger Streuverlust, höhere Conversion-Raten über alle Stages.",
        whyNow: "Weil jede Prozentpunkt-Verbesserung direkt auf den Umsatz durchschlägt.",
      };

    case "SALES_QUALITY":
      return {
        rootCause: "Discovery, Bedarfstiefe und Kaufreife werden nicht konsequent genug herausgearbeitet. Gespräche sind zu feature-lastig statt problem-fokussiert.",
        benchmarkHint: `Aktuell ${pct(metrics?.convDiscoveryToPresentation)} Discovery→Präsentation vs. Benchmark ${pct(metrics?.benchmarkDiscoveryToPresentation)}.`,
        actions: [
          "Discovery-Framework einführen: BANT, MEDDIC oder ähnliches für konsistente Qualifizierung.",
          "Pflichtfragen definieren: Pain, Budget, Timeline, Decision-Maker müssen geklärt sein.",
          "Kein Angebot ohne Qualifizierung: Klare Regel, dass nur qualifizierte Opportunities in die Angebotsphase gehen.",
        ],
        shortTermImpact: "Höherwertige Opportunities, weniger Zeit mit unqualifizierten Deals.",
        whyNow: "Weil bessere Qualifizierung die Qualität jeder weiteren Maßnahme erhöht.",
      };

    case "PROCESS":
      return {
        rootCause: "Fehlende Ownership, Next Steps und Steuerung machen die Performance instabil. Deals verlieren an Momentum, weil Follow-up nicht strukturiert ist.",
        benchmarkHint: `Aktuell ${pct(metrics?.convPresentationToDeal)} Präsentation→Deal vs. Benchmark ${pct(metrics?.benchmarkPresentationToDeal)}.`,
        actions: [
          "CRM-Disziplin einführen: Jeder Deal hat einen Owner, einen Status und einen Next Step.",
          "Follow-up-Sequenzen standardisieren: Nach Demo, nach Angebot, nach Einwand.",
          "Pipeline-Reviews etablieren: Wöchentliche Reviews mit klaren Entscheidungen (Weiter, Pause, Verloren).",
        ],
        shortTermImpact: "Mehr Abschlüsse ohne zusätzliche Leads, bessere Forecast-Genauigkeit.",
        whyNow: "Weil vorhandene Chancen aktuell ohne Not an Momentum verlieren.",
      };

    case "POSITIONING":
      return {
        rootCause: "Schwache Differenzierung und zu wenig Proof reduzieren Vertrauen, Kaufbereitschaft und Abschlusswahrscheinlichkeit.",
        benchmarkHint: `Positioning-Score: ${result.diagnosticScoring?.dimensionScores?.POSITIONING?.score || 0}/100.`,
        actions: [
          "Value Proposition schärfen: Was macht Sie einzigartig? Warum sollten Kunden Sie wählen?",
          "Case Studies erstellen: Konkrete Erfolgsgeschichten mit messbaren Ergebnissen.",
          "Referenzkunden aktivieren: Testimonials, Logos, Empfehlungen prominent nutzen.",
        ],
        shortTermImpact: "Höhere Abschlusswahrscheinlichkeit, kürzere Sales Cycles.",
        whyNow: "Weil starke Positionierung alle anderen Maßnahmen verstärkt.",
      };

    case "REP_PERFORMANCE":
      return {
        rootCause: "Die Leistungsstreuung im Team ist hoch. Top Performer arbeiten anders als der Durchschnitt, aber dieses Wissen wird nicht systematisch übertragen.",
        benchmarkHint: `Pacing vs. Plan: ${pct(result.teamPerformance?.pacingVsPlanPct || 0)}. Produktive Reps: ${result.teamPerformance?.productiveReps || 0}.`,
        actions: [
          "Top-Performer-Verhalten dokumentieren: Was machen die Besten anders?",
          "Coaching-Programm starten: Wöchentliche 1:1s mit klaren Performance-Zielen.",
          "Performance-Dashboards einführen: Transparenz über KPIs pro Rep.",
        ],
        shortTermImpact: "Höhere Durchschnittsleistung, weniger Abhängigkeit von einzelnen Stars.",
        whyNow: "Weil das Potenzial bereits im Team ist und nur aktiviert werden muss.",
      };

    default:
      return {
        rootCause: "In diesem Bereich entsteht aktuell ein relevanter Teil der Umsatzleckage.",
        benchmarkHint: "Verbesserungspotenzial identifiziert.",
        actions: [
          "Ursachen analysieren: Wo genau entsteht der Verlust?",
          "Quick Wins identifizieren: Was kann sofort verbessert werden?",
          "Maßnahmen priorisieren: Impact vs. Aufwand bewerten.",
        ],
        shortTermImpact: "Messbare Verbesserung in diesem Bereich.",
        whyNow: "Weil jede Optimierung direkt auf den Umsatz durchschlägt.",
      };
  }
}

/**
 * Builds Quick Win recommendations from ENGINE data
 */
export function buildQuickWinRecommendations(result: MasterResult): QuickWinRecommendation[] {
  if (!result) return [];

  const wins: QuickWinRecommendation[] = [];
  const team = result.teamPerformance;
  const goalPath = result.computedMetrics?.goalPath;
  const metrics = result.computedMetrics;
  const capacity = result.computedMetrics?.capacityCheck;
  const costEfficiency = result.computedMetrics?.costInefficiency;
  const funnelDiagnosis = result.computedMetrics?.funnelDiagnosis;

  // Funnel-Hebel aus Diagnose (sortiert nach größtem Gap, max. 3)
  if (funnelDiagnosis?.stages && funnelDiagnosis.stages.length > 0) {
    const sortedStages = [...funnelDiagnosis.stages]
      .filter((s) => s.gap > 0)
      .sort((a, b) => b.gap - a.gap)
      .slice(0, 3);

    for (const stage of sortedStages) {
      wins.push({
        title: `${stage.name} verbessern`,
        category: "Funnel-Optimierung",
        effort: stage.isBlocking ? "high" : "medium",
        impact: "high",
        timeframe: stage.isBlocking ? "30 Tage" : "14 Tage",
        description: stage.isBlocking
          ? `Diese Stufe ist ein struktureller Blocker: Selbst bei maximalem Optimierungspotenzial reicht die Rate nicht aus, um das Ziel zu erreichen. Es braucht mehr Volumen ODER ein angepasstes Ziel.`
          : `Gap von ${Math.round(stage.gap)} Prozentpunkten schließen. Aktuell: ${stage.actual.toFixed(1)}% → erforderlich: ${stage.required.toFixed(1)}% → Potenzial: ${stage.potential.toFixed(1)}%.`,
        steps: [
          `Aktuell: ${stage.actual.toFixed(1)}% | Erforderlich: ${stage.required > 0 ? stage.required.toFixed(1) + "%" : "–"} | Potenzial: ${stage.potential.toFixed(1)}%`,
          stage.isBlocking
            ? "Entweder Kontaktvolumen erhöhen ODER Jahresziel neu kalibrieren"
            : `Conversion um ${Math.round(stage.gap)} Prozentpunkte steigern durch gezielte Qualifizierung`,
          "Wöchentliches Tracking auf Stufen-Ebene einführen",
          "Unterperformende Reps auf diesem Schritt gezielt coachen",
        ],
      });
    }
  }

  // Capacity Planning Quick Win (höchste Priorität wenn Ziel strukturell unerreichbar)
  if (capacity?.isTargetUnreachableWithCurrentSetup) {
    wins.push({
      title: "Zielmodell an reale Kapazität anpassen",
      category: "Capacity Planning",
      effort: "medium",
      impact: "high",
      timeframe: "7 Tage",
      description:
        "Ihr Ziel liegt aktuell über der theoretischen Jahreskapazität des bestehenden Setups. Bevor mehr Druck aufgebaut wird, muss klar sein, ob Volumen, Conversion oder Ressourcen angepasst werden.",
      steps: [
        `Aktuelle theoretische Kapazität prüfen: ${formatCurrency(capacity.currentMaxRevenueCapacity || 0)}`,
        `Optimierte Kapazität prüfen: ${formatCurrency(capacity.optimizedMaxRevenueCapacity || 0)}`,
        "Entscheiden: Mehr Kontakte, bessere Conversion oder zusätzliche Kapazität",
        "Jahresziel in einen realistischen Zielpfad pro Monat und Rep übersetzen",
      ],
    });
  }

  // Budget Effizienz Quick Win (wenn Warnung aktiv und Gap groß genug)
  if (
    costEfficiency?.showBudgetEfficiencyWarning &&
    (costEfficiency.revenueEfficiencyGapAnnual || 0) > 25000
  ) {
    wins.push({
      title: "Budgeteffizienz sichtbar machen",
      category: "Unit Economics",
      effort: "low",
      impact: "high",
      timeframe: "7 Tage",
      description:
        "Machen Sie transparent, wie viel Umsatz pro Euro Sales-Kosten tatsächlich erzeugt wird und wo die Effizienzlücke zum Zielniveau liegt.",
      steps: [
        `Aktueller Umsatz pro Euro Kosten: ${Number(costEfficiency.revenuePerEuroSalary || 0).toFixed(2)} €`,
        `Rechnerisches Umsatzpotenzial: ${formatCurrency(costEfficiency.revenueEfficiencyGapAnnual || 0)} pro Jahr`,
        "Monatliches Review für Output pro Rep und pro Kostenblock etablieren",
        "Kostenwirkung nicht nur über Aktivität, sondern über Conversion und Deal-Qualität steuern",
      ],
    });
  }

  // NEUE LOGIK: Rep-Performance Quick Wins haben absolute Priorität

  // Quick Win 1: Pacing Problem
  if (team?.summarySignals?.hasPacingProblem) {
    wins.push({
      title: "Weekly Rep Scoreboard live schalten",
      category: "Performance Steering",
      effort: "low",
      impact: "high",
      timeframe: "7 Tage",
      description:
        "Machen Sie Ziel, Ist, Forecast und Zielrückstand pro Woche sichtbar – auf Team- und Rep-Ebene. Das schafft Transparenz und Verantwortlichkeit.",
      steps: [
        "Jahresziel in Monatsziele übersetzen",
        "Soll bis heute je Team und Rep berechnen",
        "Wöchentlichen Review-Rhythmus etablieren (30 Min pro Woche)",
        "Dashboard oder Excel-Sheet mit Ziel, Ist, Forecast, Gap pro Rep",
      ],
    });
  }

  // Quick Win 2: Target Breakdown Problem
  if (team?.summarySignals?.hasTargetBreakdownProblem) {
    wins.push({
      title: "Ziel-Ist-Review pro Rep einführen",
      category: "Target Management",
      effort: "low",
      impact: "high",
      timeframe: "14 Tage",
      description:
        "Brechen Sie das Jahresziel auf Quartals- und Monatsziele je Rep herunter. Jeder Rep kennt sein Ziel und seinen aktuellen Stand.",
      steps: [
        "Jahresziel auf Reps verteilen (nach Seniorität, Territory, etc.)",
        "Monatsziele je Rep definieren",
        "Ziel-Ist-Abweichung wöchentlich tracken",
        "Verantwortlichkeit verankern: Jeder Rep kennt seinen Stand",
      ],
    });
  }

  // Quick Win 3: Pipeline Problem
  if (team?.summarySignals?.hasPipelineProblem) {
    wins.push({
      title: "Pipeline-Abdeckung pro Rep transparent machen",
      category: "Pipeline Management",
      effort: "medium",
      impact: "high",
      timeframe: "14 Tage",
      description:
        "Prüfen Sie, welche Reps genug belastbare Pipeline zur Zielerreichung haben und wo strukturelle Unterdeckung besteht.",
      steps: [
        "Pipeline-Abdeckung pro Rep berechnen: Ziel vs. gewichtete Pipeline",
        "Reps nach Zielabdeckung clustern (on track, at risk, critical)",
        "Deals ohne Fortschritt bereinigen (>60 Tage keine Aktivität)",
        "Pipeline-Lücken gezielt priorisieren und schließen",
      ],
    });
  }

  // Quick Win 4: Variance Problem
  if (team?.summarySignals?.hasVarianceProblem) {
    wins.push({
      title: "Top-Performer-Pattern dokumentieren",
      category: "Best Practice",
      effort: "medium",
      impact: "high",
      timeframe: "21 Tage",
      description:
        "Dokumentieren Sie, was Top-Performer anders machen: Gesprächsführung, Follow-up, Objection Handling, Closing.",
      steps: [
        "Top 3 Performer identifizieren (nach Zielerreichung, Win Rate, Deal Size)",
        "Interviews führen: Was machen sie anders?",
        "Best Practices in Playbook übersetzen",
        "Training und Coaching auf Basis von Top-Performer-Patterns aufbauen",
      ],
    });
  }

  // Quick Win 5: Review Problem
  if (team?.summarySignals?.hasReviewProblem) {
    wins.push({
      title: "Wöchentlichen Review-Rhythmus etablieren",
      category: "Management Discipline",
      effort: "low",
      impact: "high",
      timeframe: "7 Tage",
      description:
        "Etablieren Sie einen wöchentlichen Review-Rhythmus mit standardisierter Agenda: Ziel, Ist, Forecast, Pipeline, Blocker.",
      steps: [
        "Review-Termin fixieren (z.B. Montag 9:00 Uhr, 30 Min)",
        "Agenda standardisieren: Ziel, Ist, Forecast, Pipeline, Blocker",
        "Verantwortlichkeit verankern: Jeder Rep präsentiert seinen Stand",
        "Nächste Schritte und Commitments festhalten",
      ],
    });
  }

  // Quick Win 6: Goal Path (wenn vorhanden)
  if (goalPath && goalPath.requiredMonthlyRevenueFromNow > 0) {
    wins.push({
      title: "Zielpfad bis Jahresende klar machen",
      category: "Execution Planning",
      effort: "low",
      impact: "high",
      timeframe: "3 Tage",
      description:
        "Der Reverse Funnel zeigt, was jeder Rep ab jetzt tatsächlich liefern muss, damit das verbleibende Umsatzziel mit den aktuellen Conversion-Raten noch erreichbar ist.",
      steps: [
        `Restumsatz bis Jahresende: ${formatCurrency(goalPath.remainingRevenueNeeded)}`,
        `Benötigter Monatsumsatz ab jetzt: ${formatCurrency(goalPath.requiredMonthlyRevenueFromNow)}`,
        `Benötigte Kontakte pro Monat (Team): ${goalPath.requiredContactsPerMonthFromNow.toFixed(1)}`,
        `Benötigte Kontakte pro Rep und Monat: ${goalPath.requiredContactsPerRepPerMonthFromNow?.toFixed(1) ?? goalPath.requiredContactsPerMonthFromNow.toFixed(1)}`,
        `Benötigte Deals pro Rep und Monat: ${goalPath.requiredDealsPerRepPerMonthFromNow?.toFixed(1) ?? goalPath.requiredDealsPerMonthFromNow.toFixed(1)}`,
      ],
    });
  }

  // Funnel Quick Wins (nur wenn keine Rep-Performance-Probleme dominieren)
  if (wins.length < 3 && metrics) {
    // Kontakt → Discovery Gap
    const contactGap = (metrics.benchmarkContactToDiscovery || 0) - (metrics.convContactToDiscovery || 0);
    if (contactGap > 5) {
      wins.push({
        title: "Lead-Scoring einführen",
        category: "Lead Quality",
        effort: "medium",
        impact: "high",
        timeframe: "14 Tage",
        description:
          "Führen Sie ein einfaches Lead-Scoring ein, um nur qualifizierte Leads in den Sales-Prozess zu geben.",
        steps: [
          "Scoring-Kriterien definieren (Budget, Authority, Need, Timeline)",
          "Leads nach Score clustern (A, B, C)",
          "Nur A- und B-Leads gehen in den Sales-Prozess",
          "C-Leads gehen in Nurturing oder werden disqualifiziert",
        ],
      });
    }

    // Discovery → Präsentation Gap
    const discoveryGap = (metrics.benchmarkDiscoveryToPresentation || 0) - (metrics.convDiscoveryToPresentation || 0);
    if (discoveryGap > 10) {
      wins.push({
        title: "Discovery-Qualifizierung standardisieren",
        category: "Sales Quality",
        effort: "medium",
        impact: "high",
        timeframe: "21 Tage",
        description:
          "Standardisieren Sie die Discovery-Qualifizierung: BANT, Pain Points, Decision Process, Timeline.",
        steps: [
          "Discovery-Checkliste erstellen (BANT, Pain, Decision Process)",
          "Nur qualifizierte Opportunities gehen in Präsentation",
          "Disqualifizierung als Erfolg werten (spart Zeit)",
          "Discovery-Qualität wöchentlich reviewen",
        ],
      });
    }

    // Präsentation → Deal Gap
    const presentationGap = (metrics.benchmarkPresentationToDeal || 0) - (metrics.convPresentationToDeal || 0);
    if (presentationGap > 5) {
      wins.push({
        title: "Objection Handling standardisieren",
        category: "Closing",
        effort: "medium",
        impact: "high",
        timeframe: "21 Tage",
        description:
          "Dokumentieren Sie die häufigsten Einwände und entwickeln Sie standardisierte Antworten.",
        steps: [
          "Top 5 Einwände sammeln (aus verlorenen Deals)",
          "Standardisierte Antworten entwickeln",
          "Objection Handling trainieren (Rollenspiele)",
          "Win/Loss-Analyse etablieren",
        ],
      });
    }
  }

  return wins.slice(0, 5);
}

// Helper functions
function pct(value: number | undefined | null): string {
  if (value === null || value === undefined) return "0%";
  return `${value.toFixed(0)}%`;
}

function formatCurrency(value: number | undefined | null): string {
  if (value === null || value === undefined) return "0 €";
  return `${Math.round(value).toLocaleString("de-DE")} €`;
}